import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { accountingApi } from '../services/accountingApi';
import {
  AccountingAccount,
  ExpenseCategory,
  Expense,
  IncomeEntry,
  Pagination,
  DashboardSummary,
  ProfitLossReport,
  ExpenseBreakdownRow,
  AccountsReceivableReport,
  CashFlowReport
} from '../types/accounting';
import ExpenseModal from '../components/Accounting/ExpenseModal';
import IncomeModal from '../components/Accounting/IncomeModal';
import AccountModal from '../components/Accounting/AccountModal';
import CategoryModal from '../components/Accounting/CategoryModal';
import './Accounting.css';

type MainTab = 'overview' | 'expenses' | 'income' | 'accounts' | 'reports';
type ReportTab = 'pl' | 'breakdown' | 'ar' | 'cashflow';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 2 }).format(amount || 0);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const Accounting: React.FC = () => {
  const [activeTab, setActiveTab] = useState<MainTab>('overview');

  // Shared reference data
  const [accounts, setAccounts] = useState<AccountingAccount[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  const loadReferenceData = useCallback(async () => {
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        accountingApi.getAccounts(),
        accountingApi.getExpenseCategories()
      ]);
      setAccounts(accountsRes.data.accounts);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error('Error loading accounting reference data:', error);
      toast.error('Failed to load accounts/categories');
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    try {
      setLoadingDashboard(true);
      const res = await accountingApi.getDashboard();
      setDashboard(res.data);
    } catch (error) {
      console.error('Error loading accounting dashboard:', error);
      toast.error('Failed to load accounting overview');
    } finally {
      setLoadingDashboard(false);
    }
  }, []);

  useEffect(() => {
    loadReferenceData();
    loadDashboard();
  }, [loadReferenceData, loadDashboard]);

  const refreshAll = () => {
    loadReferenceData();
    loadDashboard();
  };

  return (
    <div className="accounting-page">
      <div className="accounting-header">
        <div>
          <h1>Accounting</h1>
          <p>Income, expenses, accounts and financial reports</p>
        </div>
      </div>

      <div className="accounting-tabs">
        <button className={`accounting-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>📊 Overview</button>
        <button className={`accounting-tab ${activeTab === 'expenses' ? 'active' : ''}`} onClick={() => setActiveTab('expenses')}>💸 Expenses</button>
        <button className={`accounting-tab ${activeTab === 'income' ? 'active' : ''}`} onClick={() => setActiveTab('income')}>💵 Income</button>
        <button className={`accounting-tab ${activeTab === 'accounts' ? 'active' : ''}`} onClick={() => setActiveTab('accounts')}>🏦 Accounts &amp; Categories</button>
        <button className={`accounting-tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>📈 Reports</button>
      </div>

      {activeTab === 'overview' && (
        <OverviewTab dashboard={dashboard} loading={loadingDashboard} />
      )}

      {activeTab === 'expenses' && (
        <ExpensesTab accounts={accounts} categories={categories} onChanged={refreshAll} />
      )}

      {activeTab === 'income' && (
        <IncomeTab accounts={accounts} onChanged={refreshAll} />
      )}

      {activeTab === 'accounts' && (
        <AccountsTab accounts={accounts} categories={categories} onChanged={refreshAll} />
      )}

      {activeTab === 'reports' && (
        <ReportsTab accounts={accounts} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

const OverviewTab: React.FC<{ dashboard: DashboardSummary | null; loading: boolean }> = ({ dashboard, loading }) => {
  if (loading && !dashboard) {
    return <div className="acc-loading">Loading overview...</div>;
  }
  if (!dashboard) return null;

  return (
    <>
      <div className="acc-stats-grid">
        <div className="acc-stat-card income">
          <div className="acc-stat-label">Total Income</div>
          <div className="acc-stat-value">{formatCurrency(dashboard.totalIncome)}</div>
        </div>
        <div className="acc-stat-card expense">
          <div className="acc-stat-label">Total Expenses</div>
          <div className="acc-stat-value">{formatCurrency(dashboard.totalExpenses)}</div>
        </div>
        <div className="acc-stat-card net">
          <div className="acc-stat-label">Net Profit</div>
          <div className={`acc-stat-value ${dashboard.netProfit < 0 ? 'negative' : ''}`}>{formatCurrency(dashboard.netProfit)}</div>
        </div>
        <div className="acc-stat-card receivable">
          <div className="acc-stat-label">Outstanding Receivable</div>
          <div className="acc-stat-value">{formatCurrency(dashboard.totalOutstandingReceivable)}</div>
        </div>
        <div className="acc-stat-card balance">
          <div className="acc-stat-label">Total Cash Balance</div>
          <div className="acc-stat-value">{formatCurrency(dashboard.totalCashBalance)}</div>
        </div>
      </div>

      <div className="acc-section">
        <div className="acc-section-header">
          <h2>Account Balances</h2>
        </div>
        <div className="acc-table-wrap">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Type</th>
                <th>Opening Balance</th>
                <th>Income</th>
                <th>Expenses</th>
                <th>Current Balance</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.accounts.map(a => (
                <tr key={a.id}>
                  <td>{a.name}{a.is_default_income ? ' (default income)' : ''}</td>
                  <td><span className={`acc-badge ${a.account_type}`}>{a.account_type}</span></td>
                  <td>{formatCurrency(a.opening_balance)}</td>
                  <td className="acc-amount positive">{formatCurrency(a.manual_income_total + a.invoice_income_total)}</td>
                  <td className="acc-amount negative">{formatCurrency(a.expense_total)}</td>
                  <td className="acc-amount">{formatCurrency(a.balance)}</td>
                </tr>
              ))}
              {dashboard.accounts.length === 0 && (
                <tr><td colSpan={6} className="acc-empty-row">No accounts yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

const ExpensesTab: React.FC<{ accounts: AccountingAccount[]; categories: ExpenseCategory[]; onChanged: () => void }> = ({ accounts, categories, onChanged }) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await accountingApi.getExpenses({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        categoryId: categoryId ? parseInt(categoryId) : undefined,
        page,
        limit: 20
      });
      setExpenses(res.data.expenses);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error loading expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, categoryId, page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (expense: Expense) => {
    if (!window.confirm(`Delete this expense of ${formatCurrency(expense.amount)}?`)) return;
    try {
      await accountingApi.deleteExpense(expense.id);
      toast.success('Expense deleted');
      load();
      onChanged();
    } catch (error: any) {
      toast.error(error?.error || 'Failed to delete expense');
    }
  };

  return (
    <div className="acc-section">
      <div className="acc-section-header">
        <h2>Expenses</h2>
        <button className="acc-btn acc-btn-primary" onClick={() => { setEditing(null); setModalOpen(true); }}>+ Add Expense</button>
      </div>

      <div className="acc-filters-row">
        <div className="acc-filter-group">
          <label>From</label>
          <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
        </div>
        <div className="acc-filter-group">
          <label>To</label>
          <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
        </div>
        <div className="acc-filter-group">
          <label>Category</label>
          <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="acc-table-wrap">
        <table className="acc-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>Payee</th>
              <th>Account</th>
              <th>Description</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {expenses.map(e => (
              <tr key={e.id}>
                <td>{formatDate(e.expense_date)}</td>
                <td>{e.category_name}</td>
                <td>{e.payee || '-'}</td>
                <td>{e.account_name}</td>
                <td>{e.description || '-'}</td>
                <td className="acc-amount negative">{formatCurrency(e.amount)}</td>
                <td>
                  <div className="acc-actions-cell">
                    <button className="acc-btn acc-btn-secondary acc-btn-sm" onClick={() => { setEditing(e); setModalOpen(true); }}>Edit</button>
                    <button className="acc-btn acc-btn-danger acc-btn-sm" onClick={() => handleDelete(e)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && expenses.length === 0 && (
              <tr><td colSpan={7} className="acc-empty-row">No expenses recorded for this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="acc-pagination">
          <button className="acc-btn acc-btn-secondary acc-btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button className="acc-btn acc-btn-secondary acc-btn-sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      <ExpenseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => { load(); onChanged(); }}
        expense={editing}
        categories={categories}
        accounts={accounts}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Income
// ---------------------------------------------------------------------------

const IncomeTab: React.FC<{ accounts: AccountingAccount[]; onChanged: () => void }> = ({ accounts, onChanged }) => {
  const [income, setIncome] = useState<IncomeEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IncomeEntry | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await accountingApi.getIncomeEntries({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page,
        limit: 20
      });
      setIncome(res.data.income);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Error loading income entries:', error);
      toast.error('Failed to load income entries');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (entry: IncomeEntry) => {
    if (!window.confirm(`Delete this income entry of ${formatCurrency(entry.amount)}?`)) return;
    try {
      await accountingApi.deleteIncomeEntry(entry.id);
      toast.success('Income entry deleted');
      load();
      onChanged();
    } catch (error: any) {
      toast.error(error?.error || 'Failed to delete income entry');
    }
  };

  return (
    <div className="acc-section">
      <div className="acc-section-header">
        <h2>Manual Income Entries</h2>
        <button className="acc-btn acc-btn-primary" onClick={() => { setEditing(null); setModalOpen(true); }}>+ Add Income</button>
      </div>
      <p style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: 0 }}>
        Student invoices are counted as income automatically and don't need to be re-entered here. Use this only for other income (donations, refunds, etc.).
      </p>

      <div className="acc-filters-row">
        <div className="acc-filter-group">
          <label>From</label>
          <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
        </div>
        <div className="acc-filter-group">
          <label>To</label>
          <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
        </div>
      </div>

      <div className="acc-table-wrap">
        <table className="acc-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Source</th>
              <th>Account</th>
              <th>Description</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {income.map(i => (
              <tr key={i.id}>
                <td>{formatDate(i.income_date)}</td>
                <td>{i.source || '-'}</td>
                <td>{i.account_name}</td>
                <td>{i.description || '-'}</td>
                <td className="acc-amount positive">{formatCurrency(i.amount)}</td>
                <td>
                  <div className="acc-actions-cell">
                    <button className="acc-btn acc-btn-secondary acc-btn-sm" onClick={() => { setEditing(i); setModalOpen(true); }}>Edit</button>
                    <button className="acc-btn acc-btn-danger acc-btn-sm" onClick={() => handleDelete(i)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && income.length === 0 && (
              <tr><td colSpan={6} className="acc-empty-row">No manual income entries for this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="acc-pagination">
          <button className="acc-btn acc-btn-secondary acc-btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button className="acc-btn acc-btn-secondary acc-btn-sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}

      <IncomeModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => { load(); onChanged(); }}
        income={editing}
        accounts={accounts}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Accounts & categories
// ---------------------------------------------------------------------------

const AccountsTab: React.FC<{ accounts: AccountingAccount[]; categories: ExpenseCategory[]; onChanged: () => void }> = ({ accounts, categories, onChanged }) => {
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountingAccount | null>(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategory | null>(null);

  const handleDeactivateAccount = async (account: AccountingAccount) => {
    if (account.is_default_income) {
      toast.info("The default income account can't be deactivated");
      return;
    }
    if (!window.confirm(`Deactivate account "${account.name}"? It will be hidden from selection but its history is kept.`)) return;
    try {
      await accountingApi.deactivateAccount(account.id);
      toast.success('Account deactivated');
      onChanged();
    } catch (error: any) {
      toast.error(error?.error || 'Failed to deactivate account');
    }
  };

  return (
    <>
      <div className="acc-section">
        <div className="acc-section-header">
          <h2>Bank / Cash Accounts</h2>
          <button className="acc-btn acc-btn-primary" onClick={() => { setEditingAccount(null); setAccountModalOpen(true); }}>+ Add Account</button>
        </div>
        <div className="acc-table-wrap">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Opening Balance</th>
                <th>Current Balance</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id}>
                  <td>{a.name}{a.is_default_income ? ' (default income)' : ''}</td>
                  <td><span className={`acc-badge ${a.account_type}`}>{a.account_type}</span></td>
                  <td>{formatCurrency(a.opening_balance)}</td>
                  <td className="acc-amount">{formatCurrency(a.balance)}</td>
                  <td>
                    <div className="acc-actions-cell">
                      <button className="acc-btn acc-btn-secondary acc-btn-sm" onClick={() => { setEditingAccount(a); setAccountModalOpen(true); }}>Edit</button>
                      {!a.is_default_income && (
                        <button className="acc-btn acc-btn-danger acc-btn-sm" onClick={() => handleDeactivateAccount(a)}>Deactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && <tr><td colSpan={5} className="acc-empty-row">No accounts yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="acc-section">
        <div className="acc-section-header">
          <h2>Expense Categories</h2>
          <button className="acc-btn acc-btn-primary" onClick={() => { setEditingCategory(null); setCategoryModalOpen(true); }}>+ Add Category</button>
        </div>
        <div className="acc-table-wrap">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>
                    <button className="acc-btn acc-btn-secondary acc-btn-sm" onClick={() => { setEditingCategory(c); setCategoryModalOpen(true); }}>Edit</button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={2} className="acc-empty-row">No categories yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <AccountModal
        isOpen={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
        onSaved={onChanged}
        account={editingAccount}
      />
      <CategoryModal
        isOpen={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        onSaved={onChanged}
        category={editingCategory}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

const ReportsTab: React.FC<{ accounts: AccountingAccount[] }> = () => {
  const [reportTab, setReportTab] = useState<ReportTab>('pl');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [profitLoss, setProfitLoss] = useState<ProfitLossReport | null>(null);
  const [breakdown, setBreakdown] = useState<ExpenseBreakdownRow[]>([]);
  const [receivable, setReceivable] = useState<AccountsReceivableReport | null>(null);
  const [cashFlow, setCashFlow] = useState<CashFlowReport | null>(null);
  const [loading, setLoading] = useState(false);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      if (reportTab === 'pl') {
        const res = await accountingApi.getProfitLoss(startDate || undefined, endDate || undefined);
        setProfitLoss(res.data);
      } else if (reportTab === 'breakdown') {
        const res = await accountingApi.getExpenseBreakdown(startDate || undefined, endDate || undefined);
        setBreakdown(res.data.breakdown);
      } else if (reportTab === 'ar') {
        const res = await accountingApi.getAccountsReceivable();
        setReceivable(res.data);
      } else if (reportTab === 'cashflow') {
        const res = await accountingApi.getCashFlow(startDate || undefined, endDate || undefined);
        setCashFlow(res.data);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [reportTab, startDate, endDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const [downloading, setDownloading] = useState<'all' | ReportTab | null>(null);

  const handleDownloadPdf = async (report: 'all' | ReportTab) => {
    try {
      setDownloading(report);
      await accountingApi.downloadReportPdf(report, startDate || undefined, endDate || undefined);
    } catch (error) {
      console.error('Error downloading report PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="acc-section">
      <div className="acc-subtabs">
        <button className={`acc-subtab ${reportTab === 'pl' ? 'active' : ''}`} onClick={() => setReportTab('pl')}>Profit &amp; Loss</button>
        <button className={`acc-subtab ${reportTab === 'breakdown' ? 'active' : ''}`} onClick={() => setReportTab('breakdown')}>Expense Breakdown</button>
        <button className={`acc-subtab ${reportTab === 'ar' ? 'active' : ''}`} onClick={() => setReportTab('ar')}>Accounts Receivable</button>
        <button className={`acc-subtab ${reportTab === 'cashflow' ? 'active' : ''}`} onClick={() => setReportTab('cashflow')}>Cash Flow</button>
      </div>

      <div className="acc-filters-row">
        {reportTab !== 'ar' && (
          <>
            <div className="acc-filter-group">
              <label>From</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="acc-filter-group">
              <label>To</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </>
        )}
        <div className="acc-filter-group" style={{ flexDirection: 'row', gap: 8, marginLeft: 'auto', alignSelf: 'flex-end' }}>
          <button className="acc-btn acc-btn-secondary" disabled={downloading !== null} onClick={() => handleDownloadPdf(reportTab)}>
            {downloading === reportTab ? 'Generating...' : '📄 This Report PDF'}
          </button>
          <button className="acc-btn acc-btn-primary" disabled={downloading !== null} onClick={() => handleDownloadPdf('all')}>
            {downloading === 'all' ? 'Generating...' : '📑 All Reports PDF'}
          </button>
        </div>
      </div>

      {loading && <div className="acc-loading">Loading report...</div>}

      {!loading && reportTab === 'pl' && profitLoss && (
        <>
          <div className="acc-stats-grid">
            <div className="acc-stat-card income">
              <div className="acc-stat-label">Total Income</div>
              <div className="acc-stat-value">{formatCurrency(profitLoss.income.totalIncome)}</div>
            </div>
            <div className="acc-stat-card expense">
              <div className="acc-stat-label">Total Expenses</div>
              <div className="acc-stat-value">{formatCurrency(profitLoss.totalExpenses)}</div>
            </div>
            <div className="acc-stat-card net">
              <div className="acc-stat-label">Net Profit</div>
              <div className={`acc-stat-value ${profitLoss.netProfit < 0 ? 'negative' : ''}`}>{formatCurrency(profitLoss.netProfit)}</div>
            </div>
          </div>
          <h3 style={{ color: '#495057' }}>Expenses by Category</h3>
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead><tr><th>Category</th><th>Total</th></tr></thead>
              <tbody>
                {profitLoss.expensesByCategory.map(row => (
                  <tr key={row.category}><td>{row.category}</td><td className="acc-amount negative">{formatCurrency(row.total)}</td></tr>
                ))}
                {profitLoss.expensesByCategory.length === 0 && <tr><td colSpan={2} className="acc-empty-row">No expenses for this range</td></tr>}
              </tbody>
            </table>
          </div>
          <h3 style={{ color: '#495057' }}>Monthly Trend</h3>
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead><tr><th>Month</th><th>Income</th><th>Expense</th><th>Net</th></tr></thead>
              <tbody>
                {profitLoss.monthlyTrend.map(row => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td className="acc-amount positive">{formatCurrency(row.income)}</td>
                    <td className="acc-amount negative">{formatCurrency(row.expense)}</td>
                    <td className="acc-amount">{formatCurrency(row.income - row.expense)}</td>
                  </tr>
                ))}
                {profitLoss.monthlyTrend.length === 0 && <tr><td colSpan={4} className="acc-empty-row">No data for this range</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && reportTab === 'breakdown' && (
        <div className="acc-table-wrap">
          <table className="acc-table">
            <thead><tr><th>Category</th><th># Transactions</th><th>Total</th></tr></thead>
            <tbody>
              {breakdown.map(row => (
                <tr key={row.category_id}>
                  <td>{row.category}</td>
                  <td>{row.count}</td>
                  <td className="acc-amount negative">{formatCurrency(row.total)}</td>
                </tr>
              ))}
              {breakdown.length === 0 && <tr><td colSpan={3} className="acc-empty-row">No expenses for this range</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!loading && reportTab === 'ar' && receivable && (
        <>
          <div className="acc-stats-grid">
            <div className="acc-stat-card receivable">
              <div className="acc-stat-label">Total Outstanding</div>
              <div className="acc-stat-value">{formatCurrency(receivable.totalOutstanding)}</div>
            </div>
          </div>
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead>
                <tr><th>Student</th><th>Description</th><th>Due Date</th><th>Status</th><th>Days Overdue</th><th>Balance Due</th></tr>
              </thead>
              <tbody>
                {receivable.items.map(item => (
                  <tr key={item.id}>
                    <td>{item.student_name}</td>
                    <td>{item.fee_description}</td>
                    <td>{formatDate(item.due_date)}</td>
                    <td><span className={`acc-badge ${item.status}`}>{item.status}</span></td>
                    <td>{item.days_overdue > 0 ? item.days_overdue : 0}</td>
                    <td className="acc-amount negative">{formatCurrency(item.balance_due)}</td>
                  </tr>
                ))}
                {receivable.items.length === 0 && <tr><td colSpan={6} className="acc-empty-row">No outstanding fees 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && reportTab === 'cashflow' && cashFlow && (
        <>
          <div className="acc-stats-grid">
            <div className="acc-stat-card balance">
              <div className="acc-stat-label">Total Balance</div>
              <div className="acc-stat-value">{formatCurrency(cashFlow.totalBalance)}</div>
            </div>
            <div className="acc-stat-card income">
              <div className="acc-stat-label">Money In (range)</div>
              <div className="acc-stat-value">{formatCurrency(cashFlow.totalMoneyIn)}</div>
            </div>
            <div className="acc-stat-card expense">
              <div className="acc-stat-label">Money Out (range)</div>
              <div className="acc-stat-value">{formatCurrency(cashFlow.totalMoneyOut)}</div>
            </div>
          </div>
          <div className="acc-table-wrap">
            <table className="acc-table">
              <thead><tr><th>Account</th><th>Current Balance</th><th>In (range)</th><th>Out (range)</th><th>Net Change (range)</th></tr></thead>
              <tbody>
                {cashFlow.accounts.map(a => (
                  <tr key={a.accountId}>
                    <td>{a.accountName}</td>
                    <td className="acc-amount">{formatCurrency(a.currentBalance)}</td>
                    <td className="acc-amount positive">{formatCurrency(a.moneyIn)}</td>
                    <td className="acc-amount negative">{formatCurrency(a.moneyOut)}</td>
                    <td className={`acc-amount ${a.netChange < 0 ? 'negative' : 'positive'}`}>{formatCurrency(a.netChange)}</td>
                  </tr>
                ))}
                {cashFlow.accounts.length === 0 && <tr><td colSpan={5} className="acc-empty-row">No accounts yet</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Accounting;
